import React, { useCallback, useEffect, useId, useRef } from "react";
import styles from "./ConfirmDialog.module.css";

/**
 * ConfirmDialog — diálogo de confirmação reutilizável (padrão do design system Nexus).
 *
 * Substitui `window.confirm` em qualquer ação destrutiva ou que mereça atrito.
 * Padrão visual espelhado do antigo confirm inline do `LeadDetailDrawer`.
 *
 * Acessibilidade:
 * - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` apontando para o título.
 * - Fecha com `Escape` (com `stopPropagation` para não fechar listeners pais).
 * - Fecha com clique no overlay.
 * - Foco inicial vai para o botão "Cancelar" por segurança (evita confirmação acidental ao pressionar Enter).
 *
 * Props:
 *   isOpen          {boolean}                                — controla a visibilidade.
 *   title           {string}                                 — título do diálogo (renderizado em <h4>).
 *   message         {string|ReactNode}                       — mensagem principal (opcional).
 *   confirmLabel    {string}                                 — texto do botão de confirmação (default: "Confirmar").
 *   cancelLabel     {string}                                 — texto do botão de cancelar (default: "Cancelar").
 *   confirmVariant  {"primary" | "danger"}                   — tonalidade do botão de confirmação (default: "primary").
 *   confirmDisabled {boolean}                                — desabilita o botão de confirmar (default: false). Útil
 *                                                              quando há campo required em `children` (ex.: textarea
 *                                                              de motivo vazio). Bloqueia `onConfirm` como defesa
 *                                                              em profundidade.
 *   onConfirm       {() => void | Promise<void>}             — callback ao confirmar.
 *   onCancel        {() => void}                             — callback ao cancelar ou fechar (ESC / overlay).
 *   children        {ReactNode}                              — conteúdo opcional entre a mensagem e os botões (ex.: textarea de motivo).
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  confirmVariant = "primary",
  confirmDisabled = false,
  onConfirm,
  onCancel,
  children,
}) {
  const cancelBtnRef = useRef(null);
  const titleId = useId();

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") {
        // Impede que listeners pais (ex.: drawer) também reajam ao ESC.
        e.stopPropagation();
        onCancel?.();
      }
    },
    [isOpen, onCancel],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    // Captura na fase de capture para vencer listeners pais que escutam em bubble.
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, handleKeyDown]);

  // Foco inicial no botão Cancelar (segurança contra confirm acidental por Enter).
  useEffect(() => {
    if (isOpen) {
      cancelBtnRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const confirmClass =
    confirmVariant === "danger" ? styles.btnDangerSolid : styles.btnPrimary;

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className={styles.box} onClick={(e) => e.stopPropagation()}>
        <h4 id={titleId} className={styles.title}>
          {title}
        </h4>
        {message && <p className={styles.message}>{message}</p>}
        {children}
        <div className={styles.actions}>
          <button
            type="button"
            ref={cancelBtnRef}
            className={styles.btnSecondary}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmClass}
            onClick={confirmDisabled ? undefined : onConfirm}
            disabled={confirmDisabled}
            aria-disabled={confirmDisabled || undefined}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
